<script>
	import proj4 from 'proj4';
	import debounce from 'debounce';
	import { mapLayers, glyphs, sprite, def } from './config.js';
	import { makeStyle, sleep } from './utils.js';
	import SideBySide from './SideBySide.svelte';
	import Map from './Map.svelte';
	
	// CONFIG
	proj4.defs(def[0],def[1]);
	
	// STATE
	let map = {
		container: null,
		compare: null,
		location: ["7","-1.97650","52.97225"],
		left: {
			obj: null,
			base: 'zoomstack-outdoor',
			basePrev: null,
			overlays: []
		},
		right: {
			obj: null,
			base: 'os-1900',
			basePrev: null,
			overlays: []
		}
	};
	let style;
	let layers;
	let search = null;
	
	// FUNCTIONS
	function updateURL() {
		let hash = location.hash;
		let newhash = `#/${map.left.base}/${map.left.overlays.join(',')}/${map.right.base}/${map.right.overlays.join(',')}/${map.location.join(',')}`;
		if (hash != newhash) {
			history.pushState(undefined, undefined, newhash);
			console.log('new url');
		}
	}

	function replaceURL() {
		console.log("replacing url");
		let hash = `#/${map.left.base}/${map.left.overlays.join(',')}/${map.right.base}/${map.right.overlays.join(',')}/${map.location.join(',')}`;
		history.replaceState(undefined, undefined, hash);
	}

	async function updateLayer(map) {
		await sleep(50);
		if (map.basePrev) {
			layers[map.basePrev].forEach(layer => {
				if (map.obj.getLayer(layer.id)) {
					map.obj.removeLayer(layer.id);
				}
			});
			updateURL();
		}
		if (mapLayers.find(l => l.id == map.base)) {
			layers[map.base].forEach(layer => {
				if (!map.obj.getLayer(layer.id)) {
					map.obj.addLayer(layer, 'base');
				}
			});
			map.basePrev = map.base;
		} else {
			map.basePrev = map.base = null;
		}
	}
	
	async function updateOverlay(map, clicked = null) {
		await sleep(50);
		if (clicked) {
			let interleave = mapLayers.find(l => l.id == clicked).middle ? 'middle' : null;
			if (map.overlays.includes(clicked)) {
				layers[clicked].forEach(layer => {
					if (!map.obj.getLayer(layer.id)) {
						map.obj.addLayer(layer, interleave);
					}
				});
				updateURL();
			} else {
				layers[clicked].forEach(layer => {
					if (map.obj.getLayer(layer.id)) {
						map.obj.removeLayer(layer.id);
					}
				});
				updateURL();
			}
		} else if (map.overlays.length > 0) {
			map.overlays.forEach(id => {
				let lay = mapLayers.find(l => l.id == id);
				if (lay) {
					let interleave = lay.middle ? 'middle' : null;
					layers[id].forEach(layer => {
						if (!map.obj.getLayer(layer.id)) {
							map.obj.addLayer(layer, interleave);
						}
					});
				} else {
					let index = map.overlays.indexOf(id);
					map.overlays.splice(index, 1);
				}
			});
		}
	}
	
	async function findPlace() {
		let url = `https://api.os.uk/search/names/v1/find?query=${search}&fq=LOCAL_TYPE:City%20LOCAL_TYPE:Town%20LOCAL_TYPE:Village%20LOCAL_TYPE:Hamlet%20LOCAL_TYPE:Suburban_Area%20LOCAL_TYPE:Other_Settlement%20LOCAL_TYPE:Postcode&maxresults=1&key=wIOFrAggpSEeCJKA1i7AfGK6VqRiCgnb`;
		let response = await fetch(url);
		let json = await response.json();
		let geo = json.results[0].GAZETTEER_ENTRY;
		
		if (geo.MBR_XMIN) {
			let min = [geo.MBR_XMIN, geo.MBR_YMIN];
			let max = [geo.MBR_XMAX, geo.MBR_YMAX];
			let bbox = [
				proj4('EPSG:27700', 'WGS84', min),
				proj4('EPSG:27700', 'WGS84', max)
			];
			map.left.obj.fitBounds(bbox);
			
			map.left.obj.once('moveend', () => {
				updateURL();
			});
			
		} else {
			let xy = [geo.GEOMETRY_X, geo.GEOMETRY_Y];
			let loc = {
				center: proj4('EPSG:27700', 'WGS84', xy),
				zoom: 15
			}
			map.left.obj.flyTo(loc);

			map.left.obj.once('moveend', () => {
				updateURL();
			});
		}
	}

	async function updateAll() {
		await sleep(300);
		if (map.left.obj.isStyleLoaded() && map.right.obj.isStyleLoaded()) {
			updateLayer(map.left);
			updateLayer(map.right);
			updateOverlay(map.left);
			updateOverlay(map.right);
		
			console.log("updating layers");
			replaceURL();
		} else {
			updateAll();
		}
	} 
	
	// CODE

	// Update state based on URL
	let hash = location.hash == '' ? '' : location.hash.split('/');
	if (hash.length == 6) {
		map.left.base = hash[1];
		map.left.overlays = hash[2] == '' ? [] : hash[2].split(',');
		map.right.base = hash[3];
		map.right.overlays = hash[4] == '' ? [] : hash[4].split(',');
		map.location = hash[5].split(',');
	}

	// Generate the base style
	makeStyle(mapLayers, glyphs, sprite)
	.then(obj => {
		style = obj.style;
		layers = obj.layers;
	});
	
	// Initialise the side-by-side frame once map objects initialised
	$: if (!map.compare && map.container && map.left.obj && map.right.obj) {
		console.log("initialising map compare")
		map.compare = new Compare(map.left.obj, map.right.obj, map.container);

		updateAll();

		map.left.obj.on('moveend', debounce(() => {
			let center = map.left.obj.getCenter();
			map.location = [
				map.left.obj.getZoom().toFixed(0),
				center.lng.toFixed(5),
				center.lat.toFixed(5)
			];
			replaceURL();
		}, 100));
	}

	// Respond to URL change
	window.onpopstate = () => {
		let hash = location.hash == '' ? '' : location.hash.split('/');

		if (map.left.base != hash[1]) {
			map.left.base = hash[1];
			updateLayer(map.left);
		};
		if (map.right.base != hash[3]) {
			map.right.base = hash[3];
			updateLayer(map.right);
		};
		if (map.left.overlays.join(',') != hash[2]) {
			let prev = [...map.left.overlays];
			map.left.overlays = hash[2] == '' ? [] : hash[2].split(',');

			if (prev.length > 0) {
				prev.forEach(id => {
					if (!map.left.overlays.includes(id)) {
						updateOverlay(map.left, id);
					}
				});
			} else if (map.left.overlays > 0) {
				map.left.overlays.forEach(id => {
					if (!prev.includes(id)) {
						updateOverlay(map.left, id);
					}
				});
			}
		}
		if (map.right.overlays.join(',') != hash[4]) {
			let prev = [...map.right.overlays];
			map.right.overlays = hash[4] == '' ? [] : hash[4].split(',');

			if (prev.length > 0) {
				prev.forEach(id => {
					if (!map.right.overlays.includes(id)) {
						updateOverlay(map.right, id);
					}
				});
			} else if (map.right.overlays > 0) {
				map.right.overlays.forEach(id => {
					if (!prev.includes(id)) {
						updateOverlay(map.right, id);
					}
				});
			}
		}
		if (map.location.join(',') != hash[5]) {
			let loc = hash[5].split(',');
			map.left.obj.jumpTo({zoom: loc[0], center: [loc[1], loc[2]]});
		}
	};
</script>

<style>
	:global(body) {
		margin: 0;
		padding: 0;
		overflow: hidden;
	}
	:global(.mapboxgl-control-container button) {
		margin: 0;
	}
	select {
		width: 220px;
	}
	#menu-search input {
		width: 180px;
	}
	#menu-search button {
		width: 40px;
	}
	#menu-left {
		top: 0;
		left: 0;
	}
	#menu-right {
		top: 0;
		right: 0;
	}
	#menu-search {
		bottom: 25px;
		left: 0;
	}
	.menu {
		position: absolute;
		z-index: 10;
		background: rgba(255,255,255,0.5);
    padding: 0.8em;
		padding-bottom: 0.3em;
		font-size: 0.85rem;
	}
</style>

<div id="menu-left" class="menu">
	<select bind:value={map.left.base} on:change={updateLayer(map.left)}>
		{#each mapLayers.filter(l => !l.overlay) as layer}
		<option value={layer.id}>{layer.name}</option>
		{/each}
	</select>
	{#each mapLayers.filter(l => l.overlay) as layer}
	<label>
		<input type="checkbox" bind:group={map.left.overlays} value={layer.id} on:click={updateOverlay(map.left, layer.id)} />
		{layer.name}
	</label>
	{/each}
</div>

<div id="menu-right" class="menu">
	<select bind:value={map.right.base} on:change={updateLayer(map.right)}>
		{#each mapLayers.filter(l => !l.overlay) as layer}
		<option value={layer.id}>{layer.name}</option>
		{/each}
	</select>
	{#each mapLayers.filter(l => l.overlay) as layer}
	<label>
		<input type="checkbox" bind:group={map.right.overlays} value={layer.id} on:click={updateOverlay(map.right, layer.id)} />
		{layer.name}
	</label>
	{/each}
</div>

<form on:submit|preventDefault={findPlace}>
	<div id="menu-search" class="menu">
		<input type="text" placeholder="Find place or postcode..." bind:value={search}/><button type="submit">Go!</button>
	</div>
</form>

{#if style}
<SideBySide bind:div={map.container}>
	<Map bind:map={map.left.obj} style={style} side="left" location={{zoom: map.location[0], lon: map.location[1], lat: map.location[2]}}/>
	<Map bind:map={map.right.obj} style={style} side="right" location={{zoom: map.location[0], lon: map.location[1], lat: map.location[2]}}/>
</SideBySide>
{/if}